<template>
  <div class="sim-card-detail" v-loading="loading">
    <el-descriptions title="基本信息" :column="3" border>
      <el-descriptions-item label="ICCID">{{
        info.iccid || "-"
      }}</el-descriptions-item>
      <el-descriptions-item label="IMSI">{{
        info.imsi || "-"
      }}</el-descriptions-item>
      <el-descriptions-item label="MSISDN">{{
        info.msisdn || "-"
      }}</el-descriptions-item>
      <el-descriptions-item label="运营商">{{
        info.operator || "-"
      }}</el-descriptions-item>
      <el-descriptions-item label="状态">
        <el-tag :type="statusType" size="small">{{
          info.status || "未知"
        }}</el-tag>
      </el-descriptions-item>
      <el-descriptions-item label="计费组">{{
        info.billing_group_name || "-"
      }}</el-descriptions-item>
      <el-descriptions-item label="总流量"
        >{{ info.total_quota ?? 0 }} MB</el-descriptions-item
      >
      <el-descriptions-item label="已用流量"
        >{{ info.used_data ?? 0 }} MB</el-descriptions-item
      >
      <el-descriptions-item label="剩余流量"
        >{{ info.remaining_data ?? 0 }} MB</el-descriptions-item
      >
      <el-descriptions-item label="激活时间">{{
        info.active_time || "-"
      }}</el-descriptions-item>
      <el-descriptions-item label="过期时间">{{
        info.expire_time || "-"
      }}</el-descriptions-item>
      <el-descriptions-item label="操作" v-if="isSuperAdmin">
        <el-space>
          <el-button type="primary" size="small" @click="handleDiagnosis"
            >智能诊断</el-button
          >
          <el-button type="success" size="small" @click="handleSync"
            >同步数据</el-button
          >
        </el-space>
      </el-descriptions-item>
    </el-descriptions>

    <div class="usage-charts" v-if="usageLogs.length > 0">
      <h4 class="chart-title">每日用量 (MB)</h4>
      <div ref="usageChart" class="chart-container"></div>
    </div>
    <el-empty v-else-if="!loading" description="暂无用量日志" />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed, watch, nextTick } from "vue";
import { useStore } from "vuex";
import { ElMessage } from "element-plus";
import apiClient from "@/services/apiClient";
import { API_URLS } from "@/config";
import * as echarts from "echarts";

const props = defineProps<{
  cardId: string;
}>();

const store = useStore();
const loading = ref(false);
const info = ref<any>({});
const usageLogs = ref<any[]>([]);
const usageChart = ref<HTMLElement | null>(null);
let chartInstance: echarts.ECharts | null = null;

const statusType = computed(() => {
  const status = info.value.account_status;
  if (status === "00" || status === "0") return "success";
  if (["08", "09", "11", "8", "9"].includes(status)) return "warning";
  return "danger";
});

const isSuperAdmin = computed(() => {
  return (store.state as any).user?.role === "super_admin";
});

const handleDiagnosis = () => {
  ElMessage.success("正在对卡片进行智能诊断...");
  // 模拟诊断逻辑
  setTimeout(() => {
    ElMessage.success("诊断完成：网络状态良好，套餐余量充足。");
  }, 1500);
};

const handleSync = async () => {
  if (!props.cardId) return;
  try {
    const response = await apiClient.post(
      API_URLS.SIM_CARD_SYNC(info.value.iccid || props.cardId)
    );
    if (response.data.code === 0) {
      ElMessage.success("同步成功");
      fetchData();
    }
  } catch (error) {
    ElMessage.error("同步失败");
  }
};

const fetchData = async () => {
  if (!props.cardId) return;
  loading.value = true;
  try {
    const response = await apiClient.get(
      API_URLS.SIM_CARD_DETAIL(props.cardId)
    );
    if (response.data.code === 0) {
      info.value = response.data.data.info || {};
      usageLogs.value = response.data.data.usage_logs || [];
      await nextTick();
      initChart();
    }
  } catch (error) {
    console.error("获取卡详情失败:", error);
  } finally {
    loading.value = false;
  }
};

const initChart = () => {
  if (!usageChart.value || usageLogs.value.length === 0) return;

  if (!chartInstance) {
    chartInstance = echarts.init(usageChart.value);
  }

  const data = usageLogs.value
    .map((log) => ({
      date: log.date,
      value: parseFloat(log.usage || "0"),
    }))
    .sort((a, b) => a.date.localeCompare(b.date));

  const option = {
    tooltip: {
      trigger: "axis",
      formatter: "{b}: {c} MB",
    },
    grid: {
      left: "3%",
      right: "4%",
      bottom: "3%",
      containLabel: true,
    },
    xAxis: {
      type: "category",
      boundaryGap: false,
      data: data.map((d) => d.date),
    },
    yAxis: {
      type: "value",
      name: "流量 (MB)",
    },
    series: [
      {
        name: "用量",
        type: "line",
        smooth: true,
        data: data.map((d) => d.value),
        areaStyle: {
          opacity: 0.3,
        },
        itemStyle: {
          color: "#409EFF",
        },
      },
    ],
  };

  chartInstance.setOption(option);
};

const handleResize = () => {
  chartInstance?.resize();
};

onMounted(() => {
  fetchData();
  window.addEventListener("resize", handleResize);
});

onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
  if (chartInstance) {
    chartInstance.dispose();
    chartInstance = null;
  }
});

watch(
  () => props.cardId,
  () => {
    fetchData();
  }
);
</script>

<style scoped>
.sim-card-detail {
  padding: 20px;
  background-color: #f9f9f9;
}
.usage-charts {
  margin-top: 20px;
}
.chart-title {
  margin-bottom: 15px;
  font-size: 16px;
  font-weight: bold;
}
.chart-container {
  height: 300px;
  width: 100%;
}
</style>
